import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export default function MerchantFeed() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const testFeed = async () => {
      try {
        setStatus('loading');
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/google-merchant-feed?format=json`
        );

        let result;
        const contentType = response.headers.get('content-type');
        
        if (contentType?.includes('application/json')) {
          result = await response.json();
        } else {
          const text = await response.text();
          throw new Error(`Expected JSON but got ${contentType}. Response: ${text.substring(0, 100)}`);
        }

        if (!response.ok) {
          throw new Error(result.message || `Request failed with status ${response.status}`);
        }

        setData(result);
        setStatus('success');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setStatus('error');
      }
    };

    testFeed();
  }, []);

  return (
    <>
      <Helmet>
        <title>Google Merchant Feed - Tõuks.ee</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Google Merchant Feed Status</h1>

          {status === 'loading' && (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading feed...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg shadow p-8">
              <h2 className="text-xl font-semibold text-red-900 mb-2">❌ Error Loading Feed</h2>
              <p className="text-red-700 mb-4">{error}</p>
              <div className="bg-white p-4 rounded border border-red-200 text-sm text-gray-700 space-y-2">
                <p className="font-semibold">Troubleshooting:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Make sure JWT Verification is OFF in Edge Function settings</li>
                  <li>Environment variables SB_PROJECT_URL and SERVICE_ROLE_KEY must be set</li>
                  <li>Products table must have data with product_variants</li>
                </ul>
              </div>
            </div>
          )}

          {status === 'success' && data && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold text-green-900 mb-4">✅ Feed is Working!</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm text-gray-600">Total Products</p>
                    <p className="text-3xl font-bold text-green-600">{data.product_count}</p>
                  </div>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm text-gray-600">Last Updated</p>
                    <p className="text-sm font-mono text-gray-700">{new Date(data.updated_at).toLocaleString()}</p>
                  </div>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm text-gray-600">XML Feed URL</p>
                    <a
                      href={`${SUPABASE_URL}/functions/v1/google-merchant-feed?format=xml`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm break-all"
                    >
                      Get XML
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Sample Products ({Math.min(5, data.products.length)} of {data.product_count})</h2>
                <div className="space-y-4">
                  {data.products.slice(0, 5).map((product: any, idx: number) => (
                    <div key={idx} className="border border-gray-200 rounded p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-gray-900 flex-1">{product.title}</h3>
                        <span className={`px-2 py-1 text-xs rounded font-medium whitespace-nowrap ml-2 ${
                          product.availability === 'in stock'
                            ? 'bg-green-100 text-green-800'
                            : product.availability === 'out of stock'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {product.availability}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{product.description}</p>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">{product.brand}</span>
                        <span className="font-semibold text-gray-900">{product.price}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-3">📤 Submit to Google Merchant Center</h3>
                <p className="text-blue-800 text-sm mb-3">Copy this URL and submit it to Google Merchant Center:</p>
                <div className="bg-white p-4 rounded border border-blue-200">
                  <code className="text-xs font-mono text-gray-700 break-all">
                    {SUPABASE_URL}/functions/v1/google-merchant-feed?format=xml
                  </code>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
